import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Grid,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  GetApp as TemplateIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import CustomTypography from "../../globalStyles/fonts";
import NodeEditor from "../../components/network-design/NodeEditor";
import EdgeEditor from "../../components/network-design/EdgeEditor";
import NetworkPreview from "../../components/network-design/NetworkPreview";
import { networkDesignService } from "../../utils/services/networkDesignService";
import { supplyChainTemplate } from "../../components/network-design/networkTemplates";
import ClientAutocomplete from "../../components/clientAutocomplete";

const ManageNetworkDesign = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialData = location.state?.design || null;

  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState(null);

  // Configuration state
  const [configName, setConfigName] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [designId, setDesignId] = useState(null);

  useEffect(() => {
    if (initialData) {
      loadConfiguration(initialData);
    } else if (!designId) {
      // Load from localStorage if no designId and no initial data
      loadFromLocalStorage();
    }
  }, [initialData]);

  const loadConfiguration = (data) => {
    setDesignId(data._id);
    setConfigName(data.name || "");
    setNodes(data.nodes || []);
    setEdges(data.edges || []);
    setSelectedClient(data.client || null);
  };

  const loadFromLocalStorage = () => {
    try {
      const storedData = localStorage.getItem("networkDesignPreview");
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        setConfigName(parsedData.name || "");
        setNodes(parsedData.nodes || []);
        setEdges(parsedData.edges || []);
        setSelectedClient(parsedData.selectedClient || null);
        // Don't set designId from localStorage as this should be for new designs
      }
    } catch (error) {
      console.error("Error loading from localStorage:", error);
    }
  };

  const clearLocalStorage = () => {
    localStorage.removeItem("networkDesignPreview");
  };

  const handleNodeMove = (nodeId, column, row) => {
    setNodes(
      nodes.map((node) =>
        node.id === nodeId ? { ...node, column, row } : node
      )
    );
  };

  const validateConfiguration = () => {
    if (!configName.trim()) {
      toast.error("Please enter a configuration name");
      return false;
    }

    if (nodes.length === 0) {
      toast.error("Please add at least one node");
      return false;
    }

    // Validate edges
    const nodeIds = new Set(nodes.map((n) => n.id));
    const invalidEdges = edges.filter(
      (edge) => !nodeIds.has(edge.from) || !nodeIds.has(edge.to)
    );

    if (invalidEdges.length > 0) {
      toast.error("Some edges reference non-existent nodes");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateConfiguration()) return;

    setSaving(true);
    try {
      const configData = {
        name: configName,
        nodes,
        edges,
        metadata: {
          lastModified: new Date(),
          version: "1.0",
        },
        client: selectedClient._id,
      };

      let response;
      if (designId) {
        response = await networkDesignService.edit(designId, configData);
        toast.success("Network design updated successfully");
      } else {
        response = await networkDesignService.create(configData);
        setDesignId(response._id);
        toast.success("Network design created successfully");
      }

      // Clear localStorage after successful save
      clearLocalStorage();
    } catch (error) {
      console.error("Error saving network design:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to save network design. Please try again."
      );
    } finally {
      setSaving(false);
      navigate("/network-design");
    }
  };

  const handlePreview = () => {
    // Save current design data to localStorage for persistence
    const previewData = {
      nodes,
      edges,
      name: configName,
      selectedClient,
      designId,
    };
    localStorage.setItem("networkDesignPreview", JSON.stringify(previewData));

    navigate("/view-network-design", {
      state: { design: { nodes, edges, name: configName } },
    });
  };

  const handleLoadTemplate = () => {
    if (
      nodes.length > 0 &&
      !window.confirm(
        "Loading a template will replace your current configuration. Continue?"
      )
    ) {
      return;
    }

    setConfigName(supplyChainTemplate.name);
    setNodes(supplyChainTemplate.nodes);
    setEdges(supplyChainTemplate.edges);
    setTemplateMenuAnchor(null);
    toast.success("Template loaded successfully");
  };

  const handleBack = () => {
    // Clear localStorage when navigating back
    clearLocalStorage();
    navigate("/network-design");
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            variant="outlined"
          >
            Back
          </Button>
          <CustomTypography variant="h4">
            {designId ? "Edit" : "Create"} Network Design
          </CustomTypography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<TemplateIcon />}
            onClick={(e) => setTemplateMenuAnchor(e.currentTarget)}
          >
            Load Template
          </Button>
          <Menu
            anchorEl={templateMenuAnchor}
            open={Boolean(templateMenuAnchor)}
            onClose={() => setTemplateMenuAnchor(null)}
          >
            <MenuItem onClick={handleLoadTemplate}>
              Supply Chain Network Flow
            </MenuItem>
          </Menu>
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={handlePreview}
            disabled={nodes.length === 0}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6} md={6}>
          <TextField
            fullWidth
            label="Configuration Name"
            value={configName}
            onChange={(e) => setConfigName(e.target.value)}
            placeholder="e.g., Supply Chain Flow"
            required
            size="small"
            margin="dense"
          />
        </Grid>
        <Grid item xs={6} md={6}>
          <ClientAutocomplete
            selectedClient={selectedClient}
            onClientChange={(cl) => setSelectedClient(cl)}
            width={"100%"}
          />
        </Grid>
      </Grid>
      <Paper sx={{ mt: 1 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Nodes" />
          <Tab label="Connectors" />
          <Tab label="Preview" />
        </Tabs>

        <Box sx={{ p: 3, overflow: "auto", maxHeight: "calc(100vh - 300px)" }}>
          {activeTab === 0 && <NodeEditor nodes={nodes} setNodes={setNodes} />}
          {activeTab === 1 && (
            <EdgeEditor edges={edges} setEdges={setEdges} nodes={nodes} />
          )}
          {activeTab === 2 && (
            <NetworkPreview
              nodes={nodes}
              edges={edges}
              isEditable={true}
              onNodeMove={handleNodeMove}
            />
          )}
        </Box>
      </Paper>

      {/* Tips */}
      {activeTab != 2 ? (
        <Paper sx={{ p: 2, mt: 2, bgcolor: "#f5f5f5" }}>
          <CustomTypography variant="subtitle2" sx={{ mb: 1 }}>
            💡 Tips:
          </CustomTypography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>
              <CustomTypography variant="body2">
                Set column and row values to position nodes in a grid layout
              </CustomTypography>
            </li>
            <li>
              <CustomTypography variant="body2">
                Use parent-child relationships to create hierarchical structures
              </CustomTypography>
            </li>
            <li>
              <CustomTypography variant="body2">
                In preview mode, you can drag nodes to fine-tune their positions
              </CustomTypography>
            </li>
            <li>
              <CustomTypography variant="body2">
                Save your configuration before previewing for clients
              </CustomTypography>
            </li>
          </ul>
        </Paper>
      ) : null}
    </Box>
  );
};

export default ManageNetworkDesign;
